<template>
    <div class="card co-payment">

        <div class="section-title" style="margin-bottom:14px">Payment method</div>

        <!-- Saved cards radio (если есть) -->
        <div v-if="savedCards.length" class="ps-saved">

            <label
                v-for="card in savedCards"
                :key="card.id"
                class="ps-card"
                :class="{ 'ps-card--active': selectedMethodId === card.id }"
            >
                <input
                    v-model="selectedMethodId"
                    :value="card.id"
                    type="radio"
                    class="ps-card__radio"
                >

                <span class="ps-card__icon">
                    <svg width="32" height="22" viewBox="0 0 32 22" fill="none">
                        <rect x="0.5" y="0.5" width="31" height="21" rx="3" fill="currentColor" opacity="0.06"/>
                        <rect x="0.5" y="0.5" width="31" height="21" rx="3" stroke="currentColor" stroke-opacity="0.2"/>
                        <rect x="4" y="14" width="14" height="3" rx="1" fill="currentColor" opacity="0.4"/>
                    </svg>
                </span>

                <span class="ps-card__info">
                    <span class="ps-card__brand">
                        {{ formatBrand( card.brand ) }} <span class="ps-card__dots">••••</span> {{ card.last4 }}
                    </span>
                    <span class="ps-card__exp">
                        Expires {{ padMonth( card.exp_month ) }}/{{ String( card.exp_year ).slice( -2 ) }}
                    </span>
                </span>
            </label>

            <label
                class="ps-card ps-card--new"
                :class="{ 'ps-card--active': selectedMethodId === '' }"
            >
                <input
                    v-model="selectedMethodId"
                    value=""
                    type="radio"
                    class="ps-card__radio"
                >

                <span class="ps-card__icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                        <line x1="12" y1="5" x2="12" y2="19"/>
                        <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                </span>
                <span class="ps-card__info">
                    <span class="ps-card__brand">Use new payment method</span>
                </span>
            </label>
        </div>

        <!-- Stripe Payment Element - все методы из Dashboard settings -->
        <div v-show="selectedMethodId === ''" class="co-stripe-element">

            <div v-if="!stripeReady" class="co-stripe-loading">
                <template v-if="initFailed">
                    <div class="co-error" style="margin-bottom:10px">
                        Couldn't load the payment form. Please disable ad/privacy blockers or extensions and try again.
                    </div>
                    <button type="button" class="btn-primary" @click="retry">Retry</button>
                </template>
                <template v-else>
                    <div class="co-spinner"></div>
                    <span>Loading payment methods...</span>
                </template>
            </div>

            <div ref="paymentElement"></div>

            <div v-if="cardError" class="co-error" style="margin-top:8px">{{ cardError }}</div>
        </div>

    </div>
</template>

<script>
export default {
    name: 'PaymentStripeSubscribe',

    props: {
        publicKey   : { type: String,  required: true },
        savedCards  : { type: Array,   default: () => [] },
        amount      : { type: Number,  required: true },   // в центах (для tier='trial' = 0 на сегодня, но Stripe Elements нужен ненулевой amount target paid plan'а; передаём как есть из catalog'а)
        slug        : { type: String,  required: true },
        tier        : { type: String,  required: true },   // 'trial' | 'starter' | 'pro' | 'power'
        contact     : { type: Object,  default: () => ({}) },
        isAuth      : { type: Boolean, default: false },
        productSlug : { type: String, default: '' },
        landingSlug : { type: String, default: '' },
    },

    emits: [ 'error', 'loading' ],

    data() {
        return {
            stripe           : null,
            elements         : null,
            paymentElement   : null,
            selectedMethodId : '',    // '' = new method, 'pm_xxx' = saved card
            stripeReady      : false,
            initFailed       : false, // Elements не поднялся (блокировщик/CORS) - показываем Retry
            cardError        : '',
            baseURL          : window.baseURL || '',
        };
    },

    computed: {

        // Trial -> Elements в 'setup' mode (без amount, для SetupIntent)
        // Paid  -> Elements в 'payment' mode (с amount, для PaymentIntent)
        isTrial() {
            return this.tier === 'trial';
        },
    },

    async mounted() {

        // Default: первая saved card если есть, иначе new
        this.selectedMethodId = this.savedCards.length > 0 ? this.savedCards[0].id : '';

        await this.initPayment();
    },

    beforeUnmount() {
        clearTimeout( this._readyTimer );
    },

    watch: {

        selectedMethodId( newVal, oldVal ) {

            if( ! this.stripe || ! this.elements )
                return;

            const wasNew = oldVal === '';
            const isNew  = newVal === '';

            if( isNew && ! wasNew ) {
                this.$nextTick( () => this.mountPaymentElement() );
            } else if( ! isNew && wasNew && this.paymentElement ) {
                this.paymentElement.unmount();
            }
        },
    },

    methods: {

        formatBrand( brand ) {

            const map = {
                visa:       'VISA',
                mastercard: 'Mastercard',
                amex:       'AMEX',
                discover:   'Discover',
                diners:     'Diners Club',
                jcb:        'JCB',
                unionpay:   'UnionPay',
            };

            return map[ ( brand || '' ).toLowerCase() ] || 'Card';
        },

        padMonth( m ) {
            return String( m ).padStart( 2, '0' );
        },

        // Динамическая загрузка Stripe.js с ретраями (идентично PaymentStripe для products).
        loadStripeJs( retries = 2 ) {

            // Проверяем именно что это функция-конструктор, а не просто truthy:
            // блокировщик/чужой скрипт может оставить window.Stripe в кривом виде.
            if( typeof window.Stripe === 'function' )
                return Promise.resolve();

            return new Promise( ( resolve, reject ) => {

                const SRC = 'https://js.stripe.com/v3/';

                // Переиспользуем тег, если он уже в DOM и ещё грузится;
                // если window.Stripe уже доступен - resolve сразу.
                let script = document.querySelector( 'script[src*="js.stripe.com/v3"]' );

                if( script && typeof window.Stripe === 'function' )
                    return resolve();

                const onError = () => {
                    // Убираем сбойный тег, чтобы повторная попытка пересоздала его
                    // (иначе leftover-скрипт повиснет без новых событий).
                    script?.remove();

                    if( retries > 0 )
                        setTimeout(
                            () => this.loadStripeJs( retries - 1 ).then( resolve, reject ),
                            600
                        );
                    else
                        reject( new Error( 'Failed to load Stripe.js' ) );
                };

                // Скрипт «загрузился», но window.Stripe не функция - значит подхватили
                // не тот тег или глобал не определился. Лечим как ошибку (remove + retry),
                // чтобы не звать window.Stripe(...) -> "is not a function".
                const onLoad = () => {
                    if( typeof window.Stripe === 'function' )
                        resolve();
                    else
                        onError();
                };

                if( ! script ) {
                    script       = document.createElement( 'script' );
                    script.src   = SRC;
                    script.async = true;
                    document.head.appendChild( script );
                }

                script.addEventListener( 'load',  onLoad,  { once: true } );
                script.addEventListener( 'error', onError, { once: true } );
            });
        },

        setupStripe() {

            // Защита: не зовём window.Stripe(...), если он не функция -
            // отдаём управляемую ошибку вместо сырого "is not a function".
            if( typeof window.Stripe !== 'function' )
                throw new Error( 'Failed to load Stripe.js' );

            this.stripe = window.Stripe( this.publicKey, {
                locale: 'en',
            });

            // Mode по tier:
            //   - trial -> 'setup' (создадим SetupIntent для сохранения карты, charge будет через trial_period_days)
            //   - paid  -> 'payment' (создадим PaymentIntent, charge сегодня)
            const elementsOptions = {
                currency:              'usd',
                paymentMethodCreation: 'manual',
                payment_method_types: [ 'card' ],

                fonts: [
                    {
                        cssSrc: 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap',
                    },
                ],

                appearance: {
                    theme: 'stripe',

                    variables: {
                        colorPrimary:         '#14B8A6',
                        colorBackground:      '#FAFFFE',
                        colorText:            '#1A1A1A',
                        colorTextSecondary:   '#6B7280',
                        colorTextPlaceholder: '#9CA3AF',
                        colorDanger:          '#F97066',
                        colorSuccess:         '#22C55E',

                        fontFamily:           '"DM Sans", system-ui, sans-serif',
                        fontSizeBase:         '14px',
                        fontWeightNormal:     '400',
                        fontWeightMedium:     '500',
                        fontWeightBold:       '600',

                        borderRadius:         '10px',

                        spacingUnit:          '4px',
                        focusBoxShadow:       '0 0 0 3px rgba(20, 184, 166, 0.15)',
                        focusOutline:         'none',
                    },

                    rules: {
                        '.Input': {
                            border:          '1px solid #E5E7EB',
                            boxShadow:       'none',
                            padding:         '12px 14px',
                            color:           '#1A1A1A',
                            backgroundColor: '#FAFFFE',
                            transition:      'border-color 0.15s ease',
                        },
                        '.Input:focus': {
                            border:    '1px solid #14B8A6',
                            boxShadow: '0 0 0 3px rgba(20, 184, 166, 0.15)',
                        },
                        '.Input--invalid': {
                            border: '1px solid #F97066',
                        },
                        '.Input--invalid:focus': {
                            boxShadow: '0 0 0 3px rgba(249, 112, 102, 0.15)',
                        },
                        '.Label': {
                            fontWeight:   '500',
                            fontSize:     '13px',
                            color:        '#2D2D2D',
                            marginBottom: '6px',
                        },
                        '.Tab': {
                            border:          '1px solid #E5E7EB',
                            borderRadius:    '10px',
                            padding:         '12px',
                            backgroundColor: '#FAFFFE',
                            transition:      'all 0.15s ease',
                        },
                        '.Tab:hover': {
                            border: '1px solid #14B8A6',
                            color:  '#0F766E',
                        },
                        '.Tab--selected': {
                            border:          '1px solid #14B8A6',
                            boxShadow:       'inset 0 0 0 1px #14B8A6',
                            backgroundColor: '#F0FDFA',
                            color:           '#0F766E',
                        },
                        '.Tab--selected:focus': {
                            boxShadow: '0 0 0 3px rgba(20, 184, 166, 0.15)',
                        },
                        '.TabLabel': {
                            fontWeight: '500',
                        },
                        '.TabIcon--selected': {
                            fill: '#0F766E',
                        },
                        '.Error': {
                            color:     '#F97066',
                            fontSize:  '13px',
                            marginTop: '6px',
                        },
                        '.Block': {
                            backgroundColor: 'transparent',
                            border:          'none',
                            boxShadow:       'none',
                        },
                    },
                },
            };

            if( this.isTrial ) {
                elementsOptions.mode = 'setup';
                // setupFutureUsage для setup mode не нужен, для payment - тоже не нужен,
                // т.к. на backend save_default_payment_method='on_subscription' уже стоит
            } else {
                elementsOptions.mode               = 'payment';
                elementsOptions.amount             = this.amount;
                elementsOptions.setup_future_usage = 'off_session';
            }

            this.elements = this.stripe.elements( elementsOptions );

            this.paymentElement = this.elements.create( 'payment', {
                layout: 'tabs',
                fields: {
                    billingDetails: {
                        address: {
                            country:    'auto',
                            postalCode: 'auto',
                        },
                    },
                },
                defaultValues: {
                    billingDetails: {
                        email: this.contact.email || '',
                        address: {
                            country: this.contact.country || 'US',
                        },
                    },
                },
            });

            if( this.selectedMethodId === '' )
                this.$nextTick( () => this.mountPaymentElement() );
            else
                this.stripeReady = true;
        },

        // Загрузка Stripe.js + инициализация Elements. Вынесено для retry.
        async initPayment() {

            this.initFailed = false;

            try {
                await this.loadStripeJs();
                this.setupStripe();
            } catch( e ) {
                this.initFailed = true;
                this.$emit( 'error', e.message || 'Failed to load payment system' );
            }
        },

        // Переинициализация по кнопке Retry (после блокировки/таймаута).
        retry() {

            this.$emit( 'error', '' );

            clearTimeout( this._readyTimer );

            this.stripeReady = false;
            this.initFailed  = false;

            try { this.paymentElement?.unmount(); } catch( e ) { /* noop */ }

            this.paymentElement = null;
            this.elements       = null;
            this.stripe         = null;

            this.initPayment();
        },

        mountPaymentElement() {

            if( ! this.paymentElement || ! this.$refs.paymentElement )
                return;

            this.paymentElement.mount( this.$refs.paymentElement );

            // Watchdog: внутренний запрос Stripe Elements может быть заблокирован
            // (adblock/расширение/CORS) - тогда 'ready' не придёт и спиннер
            // висит вечно. Через 12с считаем загрузку проваленной и даём Retry.
            clearTimeout( this._readyTimer );
            this._readyTimer = setTimeout( () => {
                if( ! this.stripeReady ) {
                    this.initFailed = true;
                    this.$emit( 'error', 'Payment form is blocked. Disable ad/privacy blockers and retry.' );
                }
            }, 12000 );

            this.paymentElement.on( 'ready', () => {
                clearTimeout( this._readyTimer );
                this.stripeReady = true;
                this.initFailed  = false;
            });
            this.paymentElement.on( 'change', ( event ) => {
                this.cardError = event.error?.message || '';
            });
        },

        // Public method - вызывается родителем (SubscribeApp) через $refs
        async submit() {

            this.$emit( 'loading', true );
            this.cardError = '';

            try {

                // Validate Elements (только для new method)
                if( this.selectedMethodId === '' ) {
                    const { error: submitError } = await this.elements.submit();
                    if( submitError ) {
                        this.cardError = submitError.message;
                        this.$emit( 'loading', false );
                        return;
                    }
                }

                // Create Subscription + SetupIntent/PaymentIntent на backend
                const response = await this.$http.post( '/subscribe/intent', {
                    slug              : this.slug,
                    contact           : this.contact,
                    payment_method_id : this.selectedMethodId,
                    product_slug      : this.productSlug,
                    landing_slug      : this.landingSlug
                });
                const { status, data } = response?.data || {};

                if( status !== 'success' || ! data?.payment )
                    throw new Error( data?.message || 'Intent creation failed' );

                if( data.payment.type !== 'inline' || data.payment.gateway !== 'stripe' )
                    throw new Error( 'Unexpected gateway response' );

                const clientSecret     = data.payment.client_secret;
                const subscriptionType = data.payment.subscription_type; // 'setup' | 'payment'
                const successUrl       = `${this.baseURL}/subscribe/success/${data.hash}`;

                if( ! [ 'setup', 'payment' ].includes( subscriptionType ) )
                    throw new Error( 'Unknown subscription_type: ' + subscriptionType );

                let result;

                if( subscriptionType === 'setup' ) {

                    // Trial flow -> SetupIntent confirm
                    if( this.selectedMethodId !== '' ) {

                        // Saved card -> attach к SetupIntent для будущего charge
                        result = await this.stripe.confirmCardSetup( clientSecret, {
                            payment_method: this.selectedMethodId,
                        });

                    } else {

                        // New card -> Elements + ConfirmSetup
                        result = await this.stripe.confirmSetup({
                            elements      :     this.elements,
                            clientSecret  : clientSecret,
                            confirmParams : {
                                return_url          : successUrl,
                                payment_method_data : {
                                    billing_details : {
                                        email : this.contact.email || '',
                                    },
                                },
                            },
                            redirect      : 'if_required',
                        });
                    }

                } else {

                    // Paid flow -> PaymentIntent confirm
                    if( this.selectedMethodId !== '' ) {

                        // Saved card
                        result = await this.stripe.confirmCardPayment( clientSecret, {
                            payment_method: this.selectedMethodId,
                        });

                    } else {

                        // New card -> Elements + ConfirmPayment
                        result = await this.stripe.confirmPayment({
                            elements:     this.elements,
                            clientSecret: clientSecret,
                            confirmParams: {
                                return_url: successUrl,
                                payment_method_data: {
                                    billing_details: {
                                        email: this.contact.email || '',
                                        address: {
                                            country:     this.contact.country     || 'US',
                                            postal_code: this.contact.postal_code || '',
                                        },
                                    },
                                },
                            },
                            redirect: 'if_required',
                        });
                    }
                }

                const { error, paymentIntent, setupIntent } = result;

                if( error ) {
                    this.cardError = error.message || ( this.isTrial ? 'Setup failed' : 'Payment declined' );
                    this.$emit( 'loading', false );
                    return;
                }

                // Success без 3DS -> intent уже succeeded -> редирект
                // (с 3DS Stripe сам редиректит через return_url)
                const intent = paymentIntent || setupIntent;
                if( intent && [ 'succeeded', 'requires_capture' ].includes( intent.status ) )
                    window.location.href = successUrl;

            } catch( err ) {

                this.$emit( 'error', err?.response?.data?.data?.message
                    || err?.response?.data?.message
                    || err.message
                    || ( this.isTrial ? 'Setup failed. Please try again.' : 'Payment failed. Please try again.' ) );


                this.$emit( 'loading', false );
            }
        },
    },
};
</script>